import * as React from "react";
import { Box, Link, Typography } from "@mui/material";

const ADMIN_CLAIM = "https://brytebridge.com/penn-station/claims/is_admin";

export default function Auth0Doc({ onNavigate }) {
  return (
    <Box id="auth0" sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Auth0 & sign-in
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        <strong>Auth0</strong> is the identity provider for both the <strong>member portal</strong> and{" "}
        <strong>admin</strong>. People sign in with their Auth0 account; our <strong>API</strong> trusts
        JSON Web Tokens (JWTs) issued by Auth0. Configuration (domain, app client ID, API audience)
        comes from environment variables in each app.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 2, mb: 1 }}>
        Member portal
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
        <li>
          After sign-in, the app requests an <strong>access token</strong> for the Stack API and sends{" "}
          <strong>Authorization: Bearer …</strong> on requests.
        </li>
        <li>
          On the <strong>first</strong> successful authenticated API request, the backend{" "}
          <strong>creates or updates</strong> a row in our <strong>users</strong> table, keyed by
          Auth0&apos;s user id (<code>sub</code>). Org membership is managed separately (for example
          in Admin).
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Admin app
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
        <li>
          Staff use the same Auth0 login flow, but the admin UI only loads if the <strong>ID token</strong>{" "}
          includes this custom claim set to <strong>true</strong>:{" "}
          <Box component="code" sx={{ display: "block", fontSize: "0.8rem", mt: 0.5, mb: 0.5 }}>
            {ADMIN_CLAIM}
          </Box>
          That claim is assigned in <strong>Auth0</strong> (for example via an Action). If it&apos;s
          missing or false, the user sees <strong>Access denied</strong> and is signed out.
        </li>
        <li>
          The <strong>API</strong> enforces the same claim on <strong>/admin/…</strong> routes: the
          access token must include <code>is_admin: true</code> under that namespace, or the server
          returns forbidden.
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        API (summary)
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
        <li>
          <strong>User routes</strong> (organizations, subscriptions, events, etc.): require a valid
          Auth0 JWT; handlers scope data by org membership and permissions in application code.
        </li>
        <li>
          <strong>Admin routes</strong> (<code>/admin/…</code>): same JWT, plus the admin claim above.
        </li>
        <li>
          <strong>Auth0 Management API</strong> (creating Auth0 users, listing accounts) runs{" "}
          <strong>only on the server</strong> using a management client id/secret—never from the
          browser.
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        How user records get created
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1.25 } }}>
        <li>
          <strong>They log in (normal path):</strong> When someone uses Auth0 to sign in or sign up and
          hits the API, we <strong>provision</strong> their <strong>users</strong> row automatically
          (linked to Auth0&apos;s id).
        </li>
        <li>
          <strong>Admin → Accounts → Create Account:</strong> Creates a real <strong>Auth0</strong> user
          via the Management API (email, optional password). Use this when someone needs a login and
          should exist in Auth0 first.
        </li>
        <li>
          <strong>Admin → Users</strong> (list) or <strong>Organization → Create User</strong>: Creates
          a <strong>database user</strong> record so you can attach them to organizations. That flow
          does <strong>not</strong> by itself create their Auth0 identity. For sign-in, they still need
          an Auth0 user with that email—usually created via <strong>Accounts</strong> or when they first
          log in through Auth0.
        </li>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} paragraph>
        <strong>Quick support line:</strong> “Logins are through <strong>Auth0</strong>. Staff need the
        admin claim in Auth0 to use the admin app. To create a login for someone, use{" "}
        <strong>Accounts → Create Account</strong>, or they sign up/sign in and we create their user
        record when they first use the portal.”
      </Typography>

      {onNavigate && (
        <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/accounts")}
          >
            Accounts →
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/users")}
          >
            Users →
          </Link>
        </Box>
      )}
    </Box>
  );
}
